import React, {useState, useEffect} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ShieldIcon from '@mui/icons-material/Shield';

import { adminGetUsers } from "../../api/Api";
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';

interface PropsAdminUsers {
    token: string;
}

/** Mirrors UserModel.MARKETING_* on the API. */
type MarketingStatus = 'subscribed' | 'unconfirmed' | 'unsubscribed' | 'suppressed' | 'never';

interface User {
    id: string;
    username: string;
    email: string;
    created_at: string;
    confirmed: boolean;
    confirmed_at: string;
    admin: boolean;
    marketing_status: MarketingStatus;
    marketing_consent_at?: string | null;
    marketing_consent_source?: string | null;
    marketing_unsubscribed_at?: string | null;
}

/**
 * `subscribed` means exactly what the send guard counts as the audience, so this
 * column and the recipient count on /admin/emails can never disagree. The other
 * states exist to explain why a user is not in it: `unconfirmed` opted in but
 * never verified their address, `suppressed` is on the bounce/complaint list and
 * is skipped whatever their consent says.
 */
const marketingChip = (status: MarketingStatus, colors: ReturnType<typeof useColors>) => {
    switch (status) {
        case 'subscribed':
            return {
                label: 'Subscribed',
                color: colors.accent.green,
                background: colors.accent.greenGlow,
                border: `${colors.accent.green}55`,
            };
        case 'unconfirmed':
            return {
                label: 'Unconfirmed',
                color: colors.accent.orange,
                background: 'transparent',
                border: `${colors.accent.orange}55`,
            };
        case 'suppressed':
            return {
                label: 'Suppressed',
                color: colors.accent.red,
                background: colors.accent.redGlow,
                border: `${colors.accent.red}55`,
            };
        case 'unsubscribed':
            return {
                label: 'Unsubscribed',
                color: colors.text.muted,
                background: 'transparent',
                border: colors.border.subtle,
            };
        default:
            return null;
    }
};

const marketingTitle = (user: User) => {
    switch (user.marketing_status) {
        case 'subscribed':
        case 'unconfirmed': {
            const when = user.marketing_consent_at ? ` on ${user.marketing_consent_at}` : '';
            const where = user.marketing_consent_source ? ` via ${user.marketing_consent_source}` : '';
            const caveat = user.marketing_status === 'unconfirmed'
                ? '. Not mailable until the address is confirmed'
                : '';
            return `Opted in${when}${where}${caveat}`;
        }
        case 'unsubscribed':
            return user.marketing_unsubscribed_at
                ? `Unsubscribed on ${user.marketing_unsubscribed_at}`
                : 'Unsubscribed';
        case 'suppressed':
            return 'Address is on the suppression list, so campaigns are never sent to it';
        default:
            return 'Never opted in to product updates';
    }
};

const MarketingStatusCell = ({ user }: { user: User }) => {
    const colors = useColors();
    const chip = marketingChip(user.marketing_status, colors);

    if (!chip) {
        return (
            <Typography
                title={marketingTitle(user)}
                sx={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: '0.75rem',
                    color: colors.text.muted,
                }}
            >
                -
            </Typography>
        );
    }

    return (
        <Chip
            label={chip.label}
            size="small"
            title={marketingTitle(user)}
            sx={{
                fontFamily: '"Share Tech Mono", monospace',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                backgroundColor: chip.background,
                color: chip.color,
                border: `1px solid ${chip.border}`,
                height: '22px',
            }}
        />
    );
};

const AdminUsers = (props: PropsAdminUsers) => {
    const colors = useColors();
    const [users, setUsers] = useState<User[]>([]);
    const token = props.token;

    useEffect(() => {
        const fetchData = async () => {
            const data = await adminGetUsers(token);
            setUsers(data.users);
        }
        fetchData();
    }, []);

    return (
        <PageContainer>
            <CyberpunkTitle
                title="Users"
                subtitle={`${users.length} REGISTERED / ${users.filter((user) => user.marketing_status === 'subscribed').length} SUBSCRIBED`}
                mb="24px"
            />

            <GlowCard glowOnHover={false}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                borderBottom: `1px solid ${colors.border.accent}`,
                                '& th': {
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: colors.accent.cyan,
                                    borderBottom: 'none',
                                    padding: '14px 16px',
                                    backgroundColor: 'transparent',
                                }
                            }}>
                                <TableCell>#</TableCell>
                                <TableCell>Username</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Created At</TableCell>
                                <TableCell align="center">Confirmed</TableCell>
                                <TableCell>Confirmed At</TableCell>
                                <TableCell align="center">Product Updates</TableCell>
                                <TableCell align="center">Role</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user, idx) => (
                                <TableRow
                                    key={idx}
                                    sx={{
                                        '&:last-child td': { border: 0 },
                                        borderBottom: `1px solid ${colors.border.subtle}`,
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: `rgba(185, 28, 28, 0.04)`,
                                        },
                                        ...(user.admin && {
                                            backgroundColor: `rgba(124, 77, 255, 0.04)`,
                                        }),
                                        '& td': {
                                            borderBottom: 'none',
                                            padding: '12px 16px',
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontSize: '0.75rem',
                                            color: colors.text.muted,
                                        }}>
                                            {String(idx + 1).padStart(2, '0')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {user.admin && (
                                                <ShieldIcon sx={{ color: colors.accent.purple, fontSize: '1rem' }} />
                                            )}
                                            <Typography sx={{
                                                fontFamily: '"Orbitron", sans-serif',
                                                fontWeight: 600,
                                                color: user.admin ? colors.accent.purple : colors.text.primary,
                                            }}>
                                                {user.username}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontSize: '0.78rem',
                                            color: colors.text.secondary,
                                        }}>
                                            {user.email}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontSize: '0.75rem',
                                            color: colors.text.muted,
                                        }}>
                                            {user.created_at}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {user.confirmed
                                            ? <CheckCircleIcon sx={{ color: colors.accent.green, fontSize: '1.2rem' }} />
                                            : <CancelIcon sx={{ color: colors.text.muted, fontSize: '1.2rem' }} />
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Typography sx={{
                                            fontFamily: '"Share Tech Mono", monospace',
                                            fontSize: '0.75rem',
                                            color: colors.text.muted,
                                        }}>
                                            {user.confirmed_at || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <MarketingStatusCell user={user} />
                                    </TableCell>
                                    <TableCell align="center">
                                        {user.admin ? (
                                            <Chip
                                                label="Admin"
                                                size="small"
                                                icon={<ShieldIcon style={{ fontSize: '0.75rem' }} />}
                                                sx={{
                                                    fontFamily: '"Share Tech Mono", monospace',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.1em',
                                                    textTransform: 'uppercase',
                                                    backgroundColor: colors.accent.purpleGlow,
                                                    color: colors.accent.purple,
                                                    border: `1px solid ${colors.accent.purple}55`,
                                                    height: '22px',
                                                    '& .MuiChip-icon': { color: colors.accent.purple },
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                label="User"
                                                size="small"
                                                sx={{
                                                    fontFamily: '"Share Tech Mono", monospace',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.1em',
                                                    textTransform: 'uppercase',
                                                    backgroundColor: 'transparent',
                                                    color: colors.text.muted,
                                                    border: `1px solid ${colors.border.subtle}`,
                                                    height: '22px',
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {!users.length && (
                    <Box sx={{ textAlign: 'center', padding: '60px 0' }}>
                        <CircularProgress size={40} sx={{ color: colors.accent.cyan }} />
                    </Box>
                )}
            </GlowCard>
        </PageContainer>
    );
};

export default AdminUsers;
